import React, { useState, useEffect } from 'react';
import {
  Row, Col, Input, Button, Table, Modal, ModalHeader, ModalBody,
  Card, CardBody, Label, FormGroup
} from 'reactstrap';
import PropTypes from 'prop-types';
import { convertCurrency, formatCurrency } from './currencyUtils';

const GetProducts = ({ onProductsSelect, exchangeRates, pb, initialProducts = [] }) => {
  // State Declarations - TRY  kullanıyoruz
  const [currencies] = useState(['TRY', 'USD', 'EUR', 'GBP']);
  const [stockTypes] = useState(['Adet', 'Kg', 'Metre', 'Litre']);
  const [selectedProducts, setSelectedProducts] = useState(initialProducts);
  const [displayCurrency, setDisplayCurrency] = useState('TRY');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Manuel ürün state'ini düzelttik
  const [manualProduct, setManualProduct] = useState({
    product_name: '',
    product_code: '',
    stock_quantity: 1,
    stock_type: 'Adet',
    currency: 'TRY',
    sales_price_incl_vat: '',
    description: '',
    isManual: true
  });

  // Ürünleri getirme fonksiyonu - PocketBase entegrasyonu
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await pb.collection('products').getFullList({
          sort: '-created'
        });
        setProducts(response);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [pb]);

  // Prop'tan gelen ürünleri takip et
  useEffect(() => {
    if (initialProducts && initialProducts.length > 0) {
      setSelectedProducts(initialProducts);
    }
  }, [initialProducts]);

  // 2. Yeni useEffect - Totaller için
  useEffect(() => {
    const grandTotal = calculateGrandTotal();
    console.log('Grand total calculated:', {
      total: grandTotal[displayCurrency],
      currency: displayCurrency,
      allTotals: grandTotal // Tüm totalleri de gönderelim
    });

    // Hem ürünleri hem de hesaplanmış tüm totalleri gönderelim
    onProductsSelect(selectedProducts, displayCurrency, grandTotal);
  }, [selectedProducts, displayCurrency]);

  const handleDisplayCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    setDisplayCurrency(newCurrency);

    const grandTotal = calculateGrandTotal();
    console.log('Currency Change - Grand Total:', grandTotal);

    onProductsSelect(selectedProducts, newCurrency, grandTotal[newCurrency]);
  };


  // Para birimi dönüşüm fonksiyonu düzeltmesi
  const convertProductPrice = (price, fromCurrency, toCurrency) => {
    if (!price || !fromCurrency || !toCurrency || !exchangeRates) return price;

    // TL için düzeltme
    const normalizedFromCurrency = fromCurrency === 'TRY' ? 'TRY' : fromCurrency;
    const normalizedToCurrency = toCurrency === 'TRY' ? 'TRY' : toCurrency;

    return convertCurrency(price, normalizedFromCurrency, normalizedToCurrency, exchangeRates);
  };

  // Hesaplama detayları fonksiyonu düzeltmesi
  // Hesaplama detayları fonksiyonu güncellendi
  const getCalculationDetails = (product) => {
    if (!product.unitPrice || !product.quantity) return '';

    const unitPrice = parseFloat(product.unitPrice);
    const quantity = parseInt(product.quantity);
    const subtotal = unitPrice * quantity;
    const fromCurrency = product.currency;

    // Görüntüleme para birimine çevrilmiş değerler
    const displayUnitPrice = convertProductPrice(unitPrice, fromCurrency, displayCurrency);
    const displaySubtotal = convertProductPrice(subtotal, fromCurrency, displayCurrency);

    // Ana hesaplama detayı
    let details = `${formatCurrency(displayUnitPrice, displayCurrency)} × ${quantity} ${product.selectedStockType} = ${formatCurrency(displaySubtotal, displayCurrency)}`;

    // Eğer orijinal para birimi görüntüleme para biriminden farklıysa parantez içinde orijinal değeri de göster
    if (fromCurrency !== displayCurrency) {
      details += ` (${formatCurrency(unitPrice, fromCurrency)} × ${quantity} = ${formatCurrency(subtotal, fromCurrency)})`;
    }

    // Kur bilgisi ekle
    if (fromCurrency !== displayCurrency) {
      const rate = exchangeRates?.currencies?.[fromCurrency]?.forexSelling;
      if (rate) {
        details += ` | Kur: ${rate.toFixed(4)}`;
      }
    }

    return details;
  };


  // Ürün toplam hesaplama fonksiyonu düzeltmesi
  const calculateProductTotal = (product) => {
    if (!product.unitPrice || !product.quantity) {
      return { amount: 0, currency: product.currency, tryEquivalent: 0 };
    }

    const total = parseFloat(product.unitPrice) * parseInt(product.quantity);
    return {
      amount: total,
      currency: product.currency,
      tryEquivalent: product.currency === 'TRY' ?
        total :
        convertProductPrice(total, product.currency, 'TRY')
    };
  };

  // Genel toplam hesaplama fonksiyonu düzeltmesi
  const calculateGrandTotal = () => {
    console.log('Selected Products:', selectedProducts);
    console.log('Exchange Rates:', exchangeRates);

    return selectedProducts.reduce((totals, product) => {
      const productTotal = calculateProductTotal(product);
      console.log(`Product Total for ${product.product_name}:`, productTotal);

      currencies.forEach(currency => {
        const convertedAmount = convertProductPrice(
          productTotal.amount,
          productTotal.currency,
          currency
        );
        totals[currency] = (totals[currency] || 0) +
          (currency === productTotal.currency ? productTotal.amount : convertedAmount);
      });

      console.log('Current Totals:', totals);
      return totals;
    }, {});
  };



  // Event Handlers
  const handleProductUpdate = (productId, updates) => {
    const updatedProducts = selectedProducts.map(p =>
      p.id === productId ? { ...p, ...updates } : p
    );
    setSelectedProducts(updatedProducts);
    const grandTotal = calculateGrandTotal();
    onProductsSelect(updatedProducts, displayCurrency, grandTotal[displayCurrency]);
  };

  const handleProductRemove = (productId) => {
    const updatedProducts = selectedProducts.filter(p => p.id !== productId);
    setSelectedProducts(updatedProducts);
    const grandTotal = calculateGrandTotal();
    onProductsSelect(updatedProducts, displayCurrency, grandTotal[displayCurrency]);
  };

  // Manuel ürün ekleme fonksiyonu düzeltmesi
  const handleManualAdd = () => {
    if (!manualProduct.product_name || !manualProduct.sales_price_incl_vat) {
      alert('Ürün adı ve birim fiyat zorunludur.');
      return;
    }

    const newProduct = {
      ...manualProduct,
      id: `manual-${Date.now()}`,
      quantity: parseInt(manualProduct.stock_quantity) || 1,
      unitPrice: parseFloat(manualProduct.sales_price_incl_vat) || 0,
      selectedStockType: manualProduct.stock_type
    };

    const updatedProducts = [...selectedProducts, newProduct];
    setSelectedProducts(updatedProducts);

    const grandTotal = calculateGrandTotal();
    onProductsSelect(updatedProducts, displayCurrency, grandTotal[displayCurrency]);

    // State'i sıfırla
    setManualProduct({
      product_name: '',
      product_code: '',
      stock_quantity: 1,
      stock_type: 'Adet',
      currency: 'TRY',
      sales_price_incl_vat: '',
      description: '',
      isManual: true
    });

    setIsModalOpen(false);
  };

  // Para birimi görüntüleme fonksiyonu
  const getDisplayPrice = (amount, fromCurrency) => {
    if (!amount || !fromCurrency) return formatCurrency(0, displayCurrency);

    // Eğer görüntüleme para birimi ile ürün para birimi aynıysa direkt göster
    if (fromCurrency === displayCurrency) {
      return formatCurrency(amount, displayCurrency);
    }

    // Farklı para birimleriyse çevrim yap
    const convertedAmount = convertProductPrice(amount, fromCurrency, displayCurrency);
    return formatCurrency(convertedAmount, displayCurrency);
  };

  // Render Functions
  const renderProductTable = () => (
    <div>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <FormGroup className="mb-0">
          <Label className="me-2">Görüntüleme Para Birimi:</Label>
          <Input
            type="select"
            value={displayCurrency}
            onChange={handleDisplayCurrencyChange}  // Değişiklik burada
            style={{ width: '120px' }}
          >
            {currencies.map(currency => (
              <option key={currency} value={currency}>{currency}</option>
            ))}
          </Input>
        </FormGroup>
      </div>

      <div className="table-responsive">
        <Table className="table-borderless mb-0">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>#</th>
              <th>Ürün Bilgisi</th>
              <th style={{ width: '120px' }}>Para Birimi</th>
              <th style={{ width: '150px' }}>Birim Fiyat</th>
              <th style={{ width: '100px' }}>Miktar</th>
              <th style={{ width: '120px' }}>Birim</th>
              <th>Detaylı Hesaplama</th>
              <th style={{ width: '150px' }}>Toplam</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {selectedProducts.map((product, index) => (
              <tr key={product.id}>
                <td>{index + 1}</td>
                <td>
                  <div>
                    <h5 className="fs-14 mb-1">{product.product_name}</h5>
                    <p className="text-muted mb-0">
                      {product.product_code ? `Kod: ${product.product_code}` : ''}
                      {product.isManual ? ' (Manuel Giriş)' : ''}
                    </p>
                  </div>
                </td>
                <td>
                  <Input
                    type="select"
                    value={product.currency}
                    onChange={(e) => {
                      const newCurrency = e.target.value;
                      const convertedPrice = convertProductPrice(
                        product.unitPrice,
                        product.currency,
                        newCurrency
                      );
                      handleProductUpdate(product.id, {
                        currency: newCurrency,
                        unitPrice: convertedPrice
                      });
                    }}
                  >
                    {currencies.map(currency => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </Input>
                </td>
                <td>
                  <Input
                    type="number"
                    className="form-control"
                    value={product.unitPrice}
                    onChange={(e) => handleProductUpdate(product.id, {
                      unitPrice: parseFloat(e.target.value) || 0
                    })}
                    min="0"
                    step="0.01"
                  />
                  <small className="text-muted d-block">
                    {getDisplayPrice(product.unitPrice, product.currency)}
                  </small>
                </td>
                <td>
                  <Input
                    type="number"
                    value={product.quantity}
                    onChange={(e) => handleProductUpdate(product.id, {
                      quantity: parseInt(e.target.value) || 0
                    })}
                    min="1"
                  />
                </td>
                <td>
                  <Input
                    type="select"
                    value={product.selectedStockType}
                    onChange={(e) => handleProductUpdate(product.id, {
                      selectedStockType: e.target.value
                    })}
                  >
                    {stockTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Input>
                </td>
                <td>
                  <small className="text-muted">
                    {getCalculationDetails(product)}
                  </small>
                </td>
                <td>
                  {getDisplayPrice(
                    calculateProductTotal(product).amount,
                    product.currency
                  )}
                </td>
                <td>
                  <Button
                    color="link"
                    className="text-danger p-0"
                    onClick={() => handleProductRemove(product.id)}
                  >
                    <i className="ri-delete-bin-line"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="7" className="text-end">
                <strong>Ara Toplam:</strong>
              </td>
              <td colSpan="2">
                <strong>
                  {getDisplayPrice(
                    calculateGrandTotal()[displayCurrency],
                    displayCurrency
                  )}
                </strong>
              </td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  );

  const renderProductModal = () => (
    <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} size="xl">
      <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
        Ürün Seçimi
      </ModalHeader>
      <ModalBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="btn-group">
            <Button
              color={activeTab === 'list' ? 'primary' : 'light'}
              onClick={() => setActiveTab('list')}
              className="px-4"
            >
              <i className="ri-list-check me-1"></i>
              Ürün Listesi
            </Button>
            <Button
              color={activeTab === 'manual' ? 'primary' : 'light'}
              onClick={() => setActiveTab('manual')}
              className="px-4"
            >
              <i className="ri-add-fill me-1"></i>
              Manuel Giriş
            </Button>
          </div>
        </div>

        {activeTab === 'list' ? (
          <>
            <div className="mb-3">
              <Input
                type="text"
                placeholder="Ürün adı veya kodu ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {loading ? (
              <div className="text-center p-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
              </div>
            ) : (
              <Table hover responsive>
                <thead>
                  <tr>
                    <th>Ürün Adı</th>
                    <th>Ürün Kodu</th>
                    <th>Stok</th>
                    <th>Fiyat</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {products
                    .filter(product =>
                      product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      product.product_code?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map(product => (
                      <tr key={product.id}>
                        <td>{product.product_name}</td>
                        <td>{product.product_code}</td>
                        <td>{product.stock_quantity} {product.stock_type || 'Adet'}</td>
                        <td>
                          {formatCurrency(product.sales_price_incl_vat, product.currency || 'TRY')}
                        </td>
                        <td>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => {
                              const newProduct = {
                                ...product,
                                quantity: 1,
                                unitPrice: product.sales_price_incl_vat,
                                selectedStockType: product.stock_type || 'Adet'
                              };
                              setSelectedProducts([...selectedProducts, newProduct]);
                              onProductsSelect([...selectedProducts, newProduct]);
                              setIsModalOpen(false);
                            }}
                            disabled={selectedProducts.some(p => p.id === product.id)}
                          >
                            {selectedProducts.some(p => p.id === product.id) ? 'Seçildi' : 'Seç'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )}
          </>
        ) : (
          <div className="manual-entry-form">
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label className="form-label">
                    Ürün Adı <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={manualProduct.product_name}
                    onChange={(e) => setManualProduct({
                      ...manualProduct,
                      product_name: e.target.value
                    })}
                    placeholder="Ürün adını girin"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label className="form-label">Ürün Kodu</Label>
                  <Input
                    type="text"
                    value={manualProduct.product_code}
                    onChange={(e) => setManualProduct({
                      ...manualProduct,
                      product_code: e.target.value
                    })}
                    placeholder="Ürün kodunu girin"
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label className="form-label">Miktar</Label>
                  <Input
                    type="number"
                    value={manualProduct.stock_quantity}
                    onChange={(e) => setManualProduct({
                      ...manualProduct,
                      stock_quantity: parseInt(e.target.value) || 1
                    })}
                    min="1"
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label className="form-label">Birim</Label>
                  <Input
                    type="select"
                    value={manualProduct.stock_type}
                    onChange={(e) => setManualProduct({
                      ...manualProduct,
                      stock_type: e.target.value
                    })}
                  >
                    {stockTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Input>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label className="form-label">Para Birimi</Label>
                  <Input
                    type="select"
                    value={manualProduct.currency}
                    onChange={(e) => setManualProduct({
                      ...manualProduct,
                      currency: e.target.value
                    })}
                  >
                    {currencies.map(currency => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </Input>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <Label className="form-label">
                    Birim Fiyat <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="number"
                    value={manualProduct.sales_price_incl_vat}
                    onChange={(e) => setManualProduct({
                      ...manualProduct,
                      sales_price_incl_vat: e.target.value
                    })}
                    placeholder="Birim fiyatı girin"
                    min="0"
                    step="0.01"
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <Label className="form-label">Açıklama</Label>
                  <Input
                    type="textarea"
                    rows="3"
                    value={manualProduct.description}
                    onChange={(e) => setManualProduct({
                      ...manualProduct,
                      description: e.target.value
                    })}
                    placeholder="Ürün açıklaması girin (opsiyonel)"
                  />
                </div>
              </Col>
            </Row>

            <div className="hstack gap-2 justify-content-end mt-3">
              <Button
                color="light"
                onClick={() => {
                  setIsModalOpen(false);
                  setManualProduct({
                    product_name: '',
                    product_code: '',
                    stock_quantity: 1,
                    stock_type: 'Adet',
                    currency: 'TRY',
                    sales_price_incl_vat: '',
                    description: '',
                    isManual: true
                  });
                }}
              >
                İptal
              </Button>
              <Button
                color="primary"
                onClick={handleManualAdd}
              >
                Ürün Ekle
              </Button>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );

  return (
    <div>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Ürünler</h5>
            <Button color="primary" size="sm" onClick={() => setIsModalOpen(true)}>
              <i className="ri-add-line align-middle me-1"></i>
              Ürün Ekle
            </Button>
          </div>
          {renderProductTable()}
        </CardBody>
      </Card>
      {renderProductModal()}
    </div>
  );
};

GetProducts.propTypes = {
  onProductsSelect: PropTypes.func.isRequired, // (products, currency, total) => void
  exchangeRates: PropTypes.object.isRequired,
  pb: PropTypes.object.isRequired,
  initialProducts: PropTypes.array
};

export default GetProducts;